/**
 * Centralized Hierarchical Query Keys Factory
 *
 * Ensures all query keys follow standard naming across the entire ERP,
 * preventing duplicate caches, key mismatches, or typos.
 */
export const queryKeys = {
  // ── Masters ────────────────────────────────────────────────────────────────
  companies: {
    all: ['companies'],
    lists: () => [...queryKeys.companies.all, 'list'],
    list: (params) => [...queryKeys.companies.lists(), params],
    details: () => [...queryKeys.companies.all, 'detail'],
    detail: (id) => [...queryKeys.companies.details(), id],
  },
  branches: {
    all: ['branches'],
    lists: () => [...queryKeys.branches.all, 'list'],
    list: (params) => [...queryKeys.branches.lists(), params],
    details: () => [...queryKeys.branches.all, 'detail'],
    detail: (id) => [...queryKeys.branches.details(), id],
  },
  departments: {
    all: ['departments'],
    lists: () => [...queryKeys.departments.all, 'list'],
    list: (params) => [...queryKeys.departments.lists(), params],
    details: () => [...queryKeys.departments.all, 'detail'],
    detail: (id) => [...queryKeys.departments.details(), id],
  },
  roles: {
    all: ['roles'],
    lists: () => [...queryKeys.roles.all, 'list'],
    list: (params) => [...queryKeys.roles.lists(), params],
    details: () => [...queryKeys.roles.all, 'detail'],
    detail: (id) => [...queryKeys.roles.details(), id],
  },
  users: {
    all: ['users'],
    lists: () => [...queryKeys.users.all, 'list'],
    list: (params) => [...queryKeys.users.lists(), params],
    details: () => [...queryKeys.users.all, 'detail'],
    detail: (id) => [...queryKeys.users.details(), id],
  },

  // ── Dropdown / Combobox Options ───────────────────────────────────────────
  options: {
    all: ['options'],
    companies: (params = {}) => [...queryKeys.options.all, 'companies', params],
    branches: (params = {}) => [...queryKeys.options.all, 'branches', params],
    departments: (params = {}) => [...queryKeys.options.all, 'departments', params],
    projects: (params = {}) => [...queryKeys.options.all, 'projects', params],
    tasks: (params = {}) => [...queryKeys.options.all, 'tasks', params],
    jobs: (params = {}) => [...queryKeys.options.all, 'jobs', params],
    users: (params = {}) => [...queryKeys.options.all, 'users', params],
    roles: (params = {}) => [...queryKeys.options.all, 'roles', params],
    teams: (params = {}) => [...queryKeys.options.all, 'teams', params],
    employees: (params = {}) => [...queryKeys.options.all, 'employees', params],
  },

  // ── Dashboards ─────────────────────────────────────────────────────────────
  dashboard: {
    all: ['dashboard'],
    personal: (params = {}) => [...queryKeys.dashboard.all, 'personal', params],
    financial: (params = {}) => [...queryKeys.dashboard.all, 'financial', params],
    financialDrilldown: (params = {}) => [...queryKeys.dashboard.all, 'financial', 'drilldown', params],
    team: (params = {}) => [...queryKeys.dashboard.all, 'team', params],
    teamAvailability: (params = {}) => [...queryKeys.dashboard.all, 'team', 'availability', params],
    teamSchedule: (params = {}) => [...queryKeys.dashboard.all, 'team', 'schedule', params],
    teamSla: (params = {}) => [...queryKeys.dashboard.all, 'team', 'sla', params],
    teamFilters: (params = {}) => [...queryKeys.dashboard.all, 'team', 'filters', params],
    teamDrilldown: (params = {}) => [...queryKeys.dashboard.all, 'team', 'drilldown', params],
    calendar: (params = {}) => [...queryKeys.dashboard.all, 'calendar', params],
    calendarSettings: () => [...queryKeys.dashboard.all, 'calendar', 'settings'],
  },

  // ── Allocation ─────────────────────────────────────────────────────────────
  allocation: {
    all: ['allocation'],
    board: (params = {}) => [...queryKeys.allocation.all, 'board', params],
    unassignedTasks: (params = {}) => [...queryKeys.allocation.all, 'unassigned-tasks', params],
    jobPool: (params = {}) => [...queryKeys.allocation.all, 'job-pool', params],
    availability: (params = {}) => [...queryKeys.allocation.all, 'availability', params],
  },

  // ── Approvals ──────────────────────────────────────────────────────────────
  approval: {
    all: ['approval'],
    summary: () => [...queryKeys.approval.all, 'summary'],
    requests: (params = {}) => [...queryKeys.approval.all, 'requests', params],
    masters: (params = {}) => [...queryKeys.approval.all, 'masters', params],
    timesheet: (params = {}) => [...queryKeys.approval.all, 'timesheet', params],
    leaves: (params = {}) => [...queryKeys.approval.all, 'leaves', params],
    productivity: (params = {}) => [...queryKeys.approval.all, 'productivity', params],
  },

  // ── Project Management ────────────────────────────────────────────────────
  pm: {
    all: ['pm'],
    projects: (params = {}) => [...queryKeys.pm.all, 'projects', params],
    tasks: (params = {}) => [...queryKeys.pm.all, 'tasks', params],
    jobs: (params = {}) => [...queryKeys.pm.all, 'jobs', params],
    timesheets: (params = {}) => [...queryKeys.pm.all, 'timesheets', params],
  },

  // ── Bruno / Productivity ──────────────────────────────────────────────────
  productivity: {
    all: ['productivity'],
    dashboard: (params = {}) => [...queryKeys.productivity.all, 'dashboard', params],
    drilldown: (params = {}) => [...queryKeys.productivity.all, 'drilldown', params],
    sessions: (params = {}) => [...queryKeys.productivity.all, 'sessions', params],
    adjustments: (params = {}) => [...queryKeys.productivity.all, 'adjustments', params],
    queue: (params = {}) => [...queryKeys.productivity.all, 'queue', params],
    legend: () => [...queryKeys.productivity.all, 'legend'],
    activityApps: (params = {}) => [...queryKeys.productivity.all, 'activity-apps', params],
    activityDomains: (params = {}) => [...queryKeys.productivity.all, 'activity-domains', params],
    activityAmbient: (params = {}) => [...queryKeys.productivity.all, 'activity-ambient', params],
  },
}
